import { useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Banknote, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { buildPaystackCallbackUrl } from "@/lib/payments";
import { customerPaystackChargeAmount, customerPlatformBookingFeeAmount } from "@/lib/platform-settlements";
import { format } from "date-fns";

const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    GHS: "₵",
    NGN: "₦",
    USD: "$",
    EUR: "€",
    GBP: "£",
    ZAR: "R",
    KES: "KSh",
  };
  return symbols[currency] || currency;
};

type PayDetails = {
  appointment_id: string;
  business_id: string;
  business_name: string;
  service_name: string;
  start_datetime: string;
  end_datetime: string;
  customer_name: string;
  customer_email: string | null;
  total_price_amount: number;
  appointment_currency: string;
  appointment_group_id: string | null;
  group_total_price_amount: number | null;
  amount_paid: number;
  amount_due: number;
  confirmation_code: string;
  billing_model?: string | null;
};

export default function PayRemainingPage() {
  const { businessSlug } = useParams<{ businessSlug: string }>();
  const { toast } = useToast();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<PayDetails | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [payerEmail, setPayerEmail] = useState("");

  const currencySymbol = details
    ? getCurrencySymbol(details.appointment_currency || "GHS")
    : "₵";
  const amountDuePayTotal = details
    ? customerPaystackChargeAmount(Number(details.amount_due || 0))
    : 0;
  const platformFee = customerPlatformBookingFeeAmount();
  const totalAmount = details
    ? Number(
        details.appointment_group_id && details.group_total_price_amount != null
          ? details.group_total_price_amount
          : details.total_price_amount,
      )
    : 0;

  const fetchDetails = async () => {
    if (!businessSlug?.trim() || !code.trim()) {
      toast({ title: "Enter your booking code", variant: "destructive" });
      return;
    }
    setLoading(true);
    setNotFound(false);
    setDetails(null);
    try {
      const { data, error } = await supabase.rpc("get_appointment_for_balance_payment", {
        _business_slug: businessSlug.trim(),
        _code: code.trim().toUpperCase(),
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) {
        setNotFound(true);
        return;
      }
      const next = row as PayDetails;
      setDetails(next);
      setPayerEmail(String(next.customer_email || "").trim());
    } catch (e: any) {
      console.error("get_appointment_for_balance_payment error:", e);
      toast({
        title: "Could not load booking",
        description: e?.message || "Check the code and try again.",
        variant: "destructive",
      });
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const startPayment = async () => {
    if (!details || details.amount_due <= 0) return;
    const email = payerEmail.trim();
    // Paystack requires an email address; don't fall back to a dummy value.
    if (!email || !email.includes("@")) {
      toast({ title: "Enter a valid email address", variant: "destructive" });
      return;
    }
    setPayLoading(true);
    try {
      const callbackUrl = buildPaystackCallbackUrl(`/pay/${businessSlug}`);
      const { data, error } = await supabase.functions.invoke("paystack-init", {
        body: {
          context: {
            type: "appointment_balance",
            appointmentId: details.appointment_id,
            appointmentGroupId: details.appointment_group_id || undefined,
          },
          payerEmail: email,
          payerName: details.customer_name || null,
          callbackUrl,
        },
      });
      if (error) throw error;
      const url = String((data as any)?.authorizationUrl || "");
      if (!url) throw new Error("Missing payment link");
      setTimeout(() => window.location.assign(url), 50);
    } catch (e: any) {
      toast({
        title: "Payment could not be started",
        description: e?.message || "Try again.",
        variant: "destructive",
      });
      setPayLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="container-app py-8 max-w-lg mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground">Pay for your visit</h1>
          <p className="text-muted-foreground mt-1">
            Enter your booking code to see the amount due and pay here.
          </p>
        </div>

        <div className="bg-card rounded-xl border border-border p-4 sm:p-6 space-y-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
            <div className="space-y-2">
              <Label htmlFor="code">Booking code</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. ABC123"
                className="font-mono tracking-widest"
                onKeyDown={(e) => e.key === "Enter" && fetchDetails()}
              />
            </div>
            <Button onClick={fetchDetails} disabled={loading || !code.trim()} className="w-full sm:w-auto">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Looking up…
                </>
              ) : (
                "Find booking"
              )}
            </Button>
          </div>

          {notFound && (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/10 p-3">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-destructive">Booking not found</p>
                <p className="text-muted-foreground">Check the code and try again.</p>
              </div>
            </div>
          )}
        </div>

        {details && (
          <div className="mt-6 bg-card rounded-xl border border-border p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">{details.business_name}</p>
              <span className="font-mono text-xs font-medium text-foreground bg-muted px-2 py-1 rounded">
                {details.confirmation_code}
              </span>
            </div>
            <div className="grid gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Service</span>
                <span className="font-medium text-foreground">{details.service_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date & time</span>
                <span className="font-medium text-foreground">
                  {format(new Date(details.start_datetime), "EEE, MMM d")} •{" "}
                  {format(new Date(details.start_datetime), "h:mm a")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total</span>
                <span className="font-medium text-foreground">
                  {currencySymbol}
                  {totalAmount.toFixed(2)}
                </span>
              </div>
              {Number(details.amount_paid) > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Already paid (deposit)</span>
                  <span className="font-medium text-foreground">
                    {currencySymbol}
                    {Number(details.amount_paid).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {details.amount_due <= 0 ? (
              <div className="flex items-center gap-2 rounded-lg border border-success/20 bg-success/10 p-3">
                <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0" />
                <p className="text-sm font-medium text-foreground">This booking is already paid in full.</p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="payerEmail">Email for receipt</Label>
                  <Input
                    id="payerEmail"
                    value={payerEmail}
                    onChange={(e) => setPayerEmail(e.target.value)}
                    placeholder="you@example.com"
                    inputMode="email"
                    autoComplete="email"
                  />
                </div>
                <div className="border-t border-border pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Balance due</span>
                    <span className="font-medium text-foreground">
                      {currencySymbol}
                      {Number(details.amount_due || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Online payment fee</span>
                    <span className="font-medium text-foreground">
                      {currencySymbol}
                      {platformFee.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-border">
                    <span className="font-semibold text-foreground">Amount due</span>
                    <span className="text-xl font-bold text-foreground">
                      {currencySymbol}
                      {amountDuePayTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
                <Button
                  className="w-full"
                  size="lg"
                  disabled={payLoading || !payerEmail.trim() || !payerEmail.trim().includes("@")}
                  onClick={startPayment}
                >
                  {payLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Redirecting to payment…
                    </>
                  ) : (
                    <>
                      <Banknote className="mr-2 h-4 w-4" />
                      Pay {currencySymbol}
                      {amountDuePayTotal.toFixed(2)}
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
